import { AnsweringClient, IInstrument } from "../../clients/answeringClient";
import { describeApiError } from "../../utils/apiError";

export interface IUser {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  /**
   * `false` means the identity resolved through the auth `users` table — a
   * finished registration. It decides whether assigning an application e-mails
   * this person at all, so dropping it (as this mapping used to) left the modal
   * unable to say what would happen.
   *
   * Always known here: the search discards entries without an identity.
   */
  isInvitation: boolean;
}

export interface UserEntry {
  id: string;
  type: "existing" | "new";
  firstName: string;
  lastName: string;
  email: string;
  recaptchaToken?: string;
  /** Carried from the search so the queue can show who gets an e-mail. */
  isInvitation?: boolean;
}

export interface CreateAssignmentModalControllerDelegate {
  didGetInstruments: (assignments: IInstrument[]) => void;
  didGetUsers: (users: IUser[]) => void;
  didCreateAssignment: (assignmentId: number, payload: { name: string; instrumentId: number; dueDate: Date }) => void;
  applicationShouldLogout: () => void;
  genericError: (message: string) => void;
}

export class CreateAssignmentModalController {
  constructor(
    private delegate: CreateAssignmentModalControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {}

  public async searchInstruments(accessToken: string, filter?: string) {
    try {
      const response = await this.answeringClient.getManagedInstrumentsSearch(
        accessToken,
        { page: 1, limit: 20, filter: filter || undefined }
      );
      this.delegate.didGetInstruments(response.data.data);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.searchInstruments(newTokens.accessToken, filter);
        return;
      }
      this.delegate.genericError(
        describeApiError(error, "Não foi possível carregar os instrumentos.")
      );
    }
  }

  public async searchUsers(accessToken: string, filter?: string): Promise<void> {
    try {
      const response = await this.answeringClient.getManagedUsers(accessToken, {
        page: 1,
        limit: 15,
        filter: filter || undefined,
      });
      const data: any[] = response.data.data ?? [];
      const users: IUser[] = data
        .filter((u) => u.identity)
        .map((u) => ({
          uid: u.uid,
          firstName: u.identity.firstName,
          lastName: u.identity.lastName,
          email: u.identity.email,
          isInvitation: u.identity.isInvitation === true,
        }));
      this.delegate.didGetUsers(users);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) return;
        this.searchUsers(newTokens.accessToken, filter);
        return;
      }
      this.delegate.didGetUsers([]);
    }
  }

  public async createAssignment(
    accessToken: string,
    dueDate: Date,
    instrumentId: number,
    name: string,
    allowFeedback: boolean = false
  ) {
    try {
      const response = await this.answeringClient.createAssignment(
        accessToken,
        dueDate,
        instrumentId,
        name,
        allowFeedback
      );
      const result = response.data as { id?: number } | undefined;
      const assignmentId = result?.id;
      this.delegate.didCreateAssignment(assignmentId ?? 0, {
        name,
        instrumentId,
        dueDate,
      });
    } catch (error: any) {
      if (error?.response?.status === 401) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.createAssignment(
          newTokens.accessToken,
          dueDate,
          instrumentId,
          name,
          allowFeedback
        );
        return;
      }
      this.delegate.genericError(
        describeApiError(
          error,
          "Não foi possível criar a aplicação. Tente novamente em instantes."
        )
      );
    }
  }

  /**
   * Links one queued respondent and reports whether the e-mail that link owed
   * actually went out: `true` delivered, `false` owed and not delivered, `null`
   * none owed. Both routes carry it in the body — returning `void`, as this
   * used to, threw away the only signal that a respondent was linked without
   * ever being told.
   *
   * A rejection still means the LINK failed and is the caller's error path;
   * `false` is the opposite case, where the link stands and only the notice
   * was lost.
   */
  public async assignSingleUser(
    accessToken: string,
    user: UserEntry,
    assignmentId: number
  ): Promise<boolean | null> {
    const response =
      user.type === "existing"
        ? await this.answeringClient.addAssignmentsToUser(accessToken, user.id, [
            assignmentId,
          ])
        : await this.answeringClient.inviteUser(
            accessToken,
            user.firstName,
            user.lastName,
            user.email,
            [assignmentId],
            user.recaptchaToken ?? ""
          );

    // Absent on an older backend that still answers 204 — reads as `null`.
    return response.data?.emailDelivered ?? null;
  }


  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: { accessToken: string; refreshToken: string } =
        response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.applicationShouldLogout();
      return;
    }
  }
}
